import React, { useState, useEffect } from "react";
import { Item } from "../../../../_models/Report.model";
import { ClientItemScoring } from "./ClientItemScoring";
import { useReportContext } from "../../../../_context/ReportContext";
import { FaCamera, FaTrash } from "react-icons/fa";
import { Modal } from "react-bootstrap";
import Camera from "../../../../_camera/Camera";
import S3Uploader from "../../../../_aws/S3Uploader";

interface clientItemProps {
  item: Item;
  itemIndex: number;
  handleScoreUpdate: (itemIndex: number, newScore: number) => void;
  handleCommentUpdate: (itemIndex: number, newComment: string) => void;
  handleImageUpdate: (itemIndex: number, images: string[]) => void;
}

export const ClientItem: React.FC<clientItemProps> = ({
  item,
  itemIndex,
  handleScoreUpdate,
  handleCommentUpdate,
  handleImageUpdate,
}) => {
  const { newReport } = useReportContext();
  const [imageList, setImageList] = useState<string[]>([]);
  const [imagesToUpload, setImagesToUpload] = useState<string[]>([]);
  const [showCameraModal, setShowCameraModal] = useState(false);

  useEffect(() => {
    setImageList(item.Images || []);
  }, [item.Images]);

  const handleCapture = (imageData: string) => {
    if (!imagesToUpload.includes(imageData)) {
      setImagesToUpload(prev => [...prev, imageData]);
    }
  };

  const handleUploadSuccess = (url: string) => {
    setImageList(prevImages => [...prevImages, url]);
    handleImageUpdate(itemIndex, [...imageList, url]);
    setImagesToUpload(prev => prev.filter(image => image !== url));
  };

  const removeImage = (imageToRemove: string) => {
    const updatedImages = imageList.filter(image => image !== imageToRemove);
    setImageList(updatedImages);
    handleImageUpdate(itemIndex, updatedImages);
  };

  return (
    <>
      <div className="au-card" style={{ marginBottom: "16px", padding: "20px 24px" }}>

        {/* Main Row */}
        <div className="rp-client-item-row">

          {/* Item Name */}
          <div className="rp-client-item-name">
            <h4 style={{ margin: 0, fontSize: "1rem", fontWeight: 600, color: "#1f2937", lineHeight: 1.4 }}>
              {item.Name}
            </h4>
          </div>

          {/* Scoring Component */}
          <div style={{ flex: "0 0 auto" }}>
            <ClientItemScoring
              itemScore={item.Mark}
              updateScore={(newScore) => handleScoreUpdate(itemIndex, newScore)}
            />
          </div>

          {/* Comment & Camera Actions */}
          <div className="rp-client-item-actions">
            <div className="form-group-class" style={{ flex: 1, margin: 0 }}>
              <input
                type="text"
                placeholder="Add comments..."
                value={item.Comment || ""}
                onChange={(e) => handleCommentUpdate(itemIndex, e.target.value)}
                style={{
                  height: "42px",
                  width: "100%",
                  border: "1px solid #E5E7EB",
                  borderRadius: "8px",
                  padding: "0 14px",
                  fontSize: "0.9rem",
                  outline: "none",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.02)"
                }}
              />
            </div>

            <button
              onClick={() => setShowCameraModal(true)}
              style={{
                width: "42px",
                height: "42px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(37,99,192,0.1)",
                color: "#2563C0",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                transition: "all 0.15s",
                flexShrink: 0
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "#2563C0"; e.currentTarget.style.color = "#fff"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(37,99,192,0.1)"; e.currentTarget.style.color = "#2563C0"; }}
            >
              <FaCamera size={18} />
            </button>
          </div>
        </div>

        {/* Images Grid */}
        {(imageList.length > 0 || imagesToUpload.length > 0) && (
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginTop: "20px", paddingTop: "16px", borderTop: "1px solid #F3F4F6" }}>

            {imageList?.map((image, index) => (
              <div key={`${item.Name}${index}`} style={{ position: "relative", width: "100px", height: "100px", borderRadius: "8px", overflow: "hidden", border: "1px solid #E5E7EB" }}>
                <img src={image} alt="Report item" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                <button
                  onClick={() => removeImage(image)}
                  style={{
                    position: "absolute",
                    top: "4px",
                    right: "4px",
                    width: "24px",
                    height: "24px",
                    borderRadius: "50%",
                    background: "rgba(220,38,38,0.9)",
                    color: "white",
                    border: "none",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
                  }}
                >
                  <FaTrash size={10} />
                </button>
              </div>
            ))}

            {imagesToUpload.map((image, index) => (
              <div key={`uploading_${index}`} style={{ width: "100px", height: "100px", borderRadius: "8px", overflow: "hidden", border: "1px solid #E5E7EB", opacity: 0.7, display: "flex", alignItems: "center", justifyContent: "center", background: "#F9FAFB" }}>
                <S3Uploader
                  image={image}
                  imageName={`${Date.now()}.jpg`}
                  folderName={newReport.ImageDirectory}
                  s3ClientName={`${newReport.Client.Name}-${newReport.ClientName}`}
                  bucketName={process.env.REACT_APP_AWS_S3_BUCKET_NAME as string}
                  onUploadSuccess={handleUploadSuccess}
                  onUploadError={(error) => console.error("Upload failed:", error)}
                />
              </div>
            ))}

          </div>
        )}
      </div>

      <Modal
        show={showCameraModal}
        onHide={() => setShowCameraModal(false)}
        centered
        className="modal-lg"
      >
        <div style={{ padding: "20px 24px", borderBottom: "1px solid #E5E7EB", display: "flex", justifyContent: "space-between", alignItems: "center", background: "#f8fafc", borderRadius: "8px 8px 0 0" }}>
          <h3 style={{ margin: 0, fontWeight: 700, fontSize: "1.25rem", color: "#0f1c34" }}>Take a photo</h3>
          <button
            onClick={() => setShowCameraModal(false)}
            style={{ background: "none", border: "none", fontSize: "1.75rem", color: "#64748b", cursor: "pointer", lineHeight: 1, padding: 0 }}
          >
            &times;
          </button>
        </div>
        <Modal.Body style={{ padding: 0, background: "#000", borderBottomLeftRadius: "8px", borderBottomRightRadius: "8px", overflow: "hidden" }}>
          <Camera
            onCapture={handleCapture}
            onCaptureCompleted={() => setShowCameraModal(false)}
          />
        </Modal.Body>
      </Modal>
    </>
  );
};
